import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Search, Clock, MessageSquare, User, LogOut, ChevronDown, Shield, X, Menu, Bus, Car, ClipboardList } from 'lucide-react';
import { RiversideMark } from './brand/RiversideMark';
import { useCelestialMode } from '../hooks/useCelestialMode';
import { useAuth } from '../context/AuthContext';
import { isAdminEmail } from '../utils/admin';
import { isTeamEmail } from '../utils/team';
import { supabase } from '../config/supabase';
import { getDisplayName } from '../utils/profileUtils';
import { AnimatePresence, motion } from 'framer-motion';
import { isSharedTransfersEnabled, isTeamPanelEnabled } from '../config/features';
import { isSamsungInternet } from '../utils/samsungInternet';

const Header = () => {
  const location = useLocation();
  const { user, logout, isAuthenticated } = useAuth();
  const isAdmin = isAdminEmail(user?.email);
  // Team ops link: shown to the team account, and to admins so they can preview it.
  const showTeamLink = isTeamPanelEnabled() && (isTeamEmail(user?.email) || isAdmin);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const isCompactHeader = isScrolled;
  const [userProfile, setUserProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const mobileMenuRef = useRef(null);
  const userMenuRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const loadUserProfile = async () => {
      if (!user?.id) return;
      setLoadingProfile(true);
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();
        if (error && error.code !== 'PGRST116') {
          console.error('Error loading profile:', error);
        } else {
          setUserProfile(data);
        }
      } catch (error) {
        console.error('Error loading profile:', error);
      } finally {
        setLoadingProfile(false);
      }
    };
    loadUserProfile();
  }, [user?.id]);

  const getDisplayNameForUser = () => {
    if (userProfile) return getDisplayName(userProfile);
    return user?.user_metadata?.name || 'User';
  };

  const sharedEnabled = isSharedTransfersEnabled();

  // Launch products lead the nav so they're always one tap away — Home stays
  // reachable via the logo. Shared Transfers entries are hidden until the
  // launch flag is enabled.
  const navItems = [
    { path: '/bus', label: 'Bus Tickets', icon: Bus },
    { path: '/private-transfer', label: 'Private Transfer', icon: Car },
    { path: '/trips', label: 'Shared Trips', icon: Search, sharedOnly: true },
    { path: '/my-trips', label: 'My Trips', icon: Clock, protected: true },
    { path: '/messages', label: 'Contact', icon: MessageSquare, protected: true },
  ].filter((item) => !item.sharedOnly || sharedEnabled);

  useEffect(() => {
    setMobileMenuOpen(false);
    setUserMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (mobileMenuOpen && mobileMenuRef.current && !mobileMenuRef.current.contains(e.target)) {
        setMobileMenuOpen(false);
      }
      if (userMenuOpen && userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobileMenuOpen, userMenuOpen]);

  const handleLogout = () => {
    logout();
    setUserMenuOpen(false);
    setMobileMenuOpen(false);
  };

  const celestialMode = useCelestialMode();
  const navPad = isCompactHeader ? 'py-2' : 'py-4';
  // Samsung Internet stops painting the page under a fixed backdrop-filter.
  // A solid bar keeps the booking form visible after a time is selected.
  const navSurface = isCompactHeader
    ? (isSamsungInternet()
      ? 'bg-white shadow-md'
      : 'bg-white/95 backdrop-blur-md shadow-md')
    : 'bg-[#A44432]';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navSurface} ${navPad}`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between gap-2">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 group shrink-0">
          <RiversideMark
            mode={celestialMode}
            className={`shrink-0 h-10 w-16 md:h-[50px] md:w-20 ${isCompactHeader ? 'text-[#A44432]' : 'text-white'}`}
          />
          <span className={`font-bold font-serif text-xl ${isCompactHeader ? 'text-[#A44432]' : 'text-white'}`}>
            Riverside
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-0.5">
          {navItems.map((item) => {
            if (item.protected && !isAuthenticated) return null;
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                title={item.label}
                className={`rounded-lg font-medium transition-colors flex items-center gap-1.5 px-4 py-2 text-sm ${
                  isActive
                    ? (isCompactHeader ? 'bg-[#A44432] text-white' : 'bg-white text-[#A44432]')
                    : (isCompactHeader ? 'text-stone-600 hover:bg-stone-100' : 'text-white/80 hover:bg-white/10')
                }`}
              >
                <Icon size={16} />
                {item.label}
              </Link>
            );
          })}

          <div className={`w-px h-5 mx-1 ${isCompactHeader ? 'bg-stone-200' : 'bg-white/20'}`} />

          {isAuthenticated ? (
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className={`rounded-full transition-colors flex items-center gap-1 p-2 ${
                  isCompactHeader ? 'hover:bg-stone-100 text-stone-600' : 'hover:bg-white/10 text-white'
                }`}
              >
                <User size={20} />
                <ChevronDown size={14} className={`transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              <AnimatePresence>
                {userMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute right-0 mt-4 w-52 bg-white rounded-2xl shadow-2xl border border-stone-100 py-2 overflow-hidden"
                  >
                    <div className="px-4 py-2 border-b border-stone-100">
                      <div className="text-sm font-bold text-stone-800 truncate">{loadingProfile ? '...' : getDisplayNameForUser()}</div>
                      <div className="text-xs text-stone-400 truncate">{user?.email}</div>
                    </div>
                    <Link to="/profile" onClick={() => setUserMenuOpen(false)} className="w-full px-4 py-2 text-left text-sm hover:bg-stone-50 flex items-center gap-2 text-stone-700">
                      <User size={14} /> Profile
                    </Link>
                    {isAdmin && (
                      <Link to="/admin" onClick={() => setUserMenuOpen(false)} className="w-full px-4 py-2 text-left text-sm hover:bg-stone-50 flex items-center gap-2 text-[#A44432] font-bold">
                        <Shield size={14} /> Admin Panel
                      </Link>
                    )}
                    {showTeamLink && (
                      <Link to="/team" onClick={() => setUserMenuOpen(false)} className="w-full px-4 py-2 text-left text-sm hover:bg-stone-50 flex items-center gap-2 text-[#A44432] font-bold">
                        <ClipboardList size={14} /> Team Ops
                      </Link>
                    )}
                    <hr className="my-1 border-stone-100" />
                    <button onClick={handleLogout} className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 text-red-600 flex items-center gap-2">
                      <LogOut size={14} /> Sign Out
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Link
                to="/login"
                className={`rounded-lg font-medium transition-colors px-4 py-2 text-sm ${
                  isCompactHeader ? 'text-stone-600 hover:bg-stone-100' : 'text-white/80 hover:bg-white/10'
                }`}
              >
                Sign In
              </Link>
              <Link
                to="/register"
                className={`rounded-xl font-medium transition-colors px-4 py-2 text-sm ${
                  isCompactHeader ? 'bg-[#A44432] text-white hover:bg-[#8B3929]' : 'bg-white text-[#A44432] hover:bg-stone-100'
                }`}
              >
                Sign Up
              </Link>
            </div>
          )}
        </div>

        {/* Mobile Toggle */}
        <button
          className="md:hidden p-2 rounded-lg"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
        >
          {mobileMenuOpen
            ? <X size={22} className={isCompactHeader ? 'text-stone-800' : 'text-white'} />
            : <Menu size={22} className={isCompactHeader ? 'text-stone-800' : 'text-white'} />
          }
        </button>
      </div>

      {/* Mobile Drawer */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            ref={mobileMenuRef}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-b border-stone-200 shadow-xl overflow-hidden"
          >
            <div className="p-4 space-y-2">
              {navItems.map((item) => {
                if (item.protected && !isAuthenticated) return null;
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`w-full flex items-center gap-4 px-4 py-3 rounded-xl transition-colors ${
                      isActive ? 'bg-[#A44432] text-white' : 'text-stone-600 active:bg-stone-50'
                    }`}
                  >
                    <Icon size={20} />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                );
              })}

              <hr className="my-2 border-stone-100" />

              {isAuthenticated ? (
                <>
                  <Link to="/profile" onClick={() => setMobileMenuOpen(false)} className="w-full flex items-center gap-4 px-4 py-3 rounded-xl text-stone-600 active:bg-stone-50">
                    <User size={20} /> <span className="font-medium">Profile</span>
                  </Link>
                  {isAdmin && (
                    <Link to="/admin" onClick={() => setMobileMenuOpen(false)} className="w-full flex items-center gap-4 px-4 py-3 rounded-xl text-[#A44432] font-bold active:bg-stone-50">
                      <Shield size={20} /> <span className="font-medium">Admin Panel</span>
                    </Link>
                  )}
                  {showTeamLink && (
                    <Link to="/team" onClick={() => setMobileMenuOpen(false)} className="w-full flex items-center gap-4 px-4 py-3 rounded-xl text-[#A44432] font-bold active:bg-stone-50">
                      <ClipboardList size={20} /> <span className="font-medium">Team Ops</span>
                    </Link>
                  )}
                  <button onClick={handleLogout} className="w-full flex items-center gap-4 px-4 py-3 rounded-xl text-red-600 active:bg-red-50">
                    <LogOut size={20} /> <span className="font-medium">Sign Out</span>
                  </button>
                </>
              ) : (
                <div className="flex gap-3 px-4 pt-2">
                  <Link to="/login" className="flex-1 text-center py-2.5 text-sm font-medium border border-stone-200 rounded-xl text-stone-600 hover:bg-stone-50">
                    Sign In
                  </Link>
                  <Link to="/register" className="flex-1 text-center py-2.5 text-sm font-medium bg-[#A44432] text-white rounded-xl hover:bg-[#8B3929]">
                    Sign Up
                  </Link>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Header;
